import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  Box,
  Button,
  TextField,
  makeStyles,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
  Snackbar,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DateTimeAntd from '../input/DateTimeAntd';
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip as ChartTooltip,
  Filler,
  LineController,
  BarController
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import PrintIcon from '@material-ui/icons/Print';
import SearchIcon from '@material-ui/icons/Search';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import TimerIcon from '@material-ui/icons/Timer';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import SpeedIcon from '@material-ui/icons/Speed';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { reportFuelSummary, reportFuelDrainFill } from 'src/features/reportDNSlice';
import { getDetailDevicePosition, getOilStatistic } from 'src/features/deviceSlice';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ChartTooltip,
  Filler,
  LineController,
  BarController
);

const crosshairPlugin = {
  id: 'activityCrosshair',
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    const active = chart.tooltip?._active;
    if (!active || !active.length) return;
    const x = active[0].element.x;
    ctx.save();

    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#cbd5e1';
    ctx.stroke();
    ctx.restore();
  },
};

const CHART_PLUGINS = [crosshairPlugin];



const parseEngineOnTime = val => {
  if (!val) return 0;
  if (typeof val === 'string' && val.includes(':')) {
    const parts = val.split(':').map(Number);
    if (parts.length >= 2) {
      return (parts[0] || 0) * 60 + (parts[1] || 0);
    }
  }
  if (!isNaN(val)) {
    return Math.floor(Number(val) / 60);
  }
  return 0;
};

const formatMinutes = totalMin => {
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const buildChartFromEvents = (events, summaryRows, positions, oilRows, firstTime, lastTime) => {
  const isMultiDay = moment.unix(firstTime).format('DD/MM/YYYY') !== moment.unix(lastTime).format('DD/MM/YYYY');
  const formatStr = isMultiDay ? 'HH:mm DD/MM' : 'HH:mm';

  const firstRow = summaryRows[0] || {};
  const startLevel = Number(firstRow.startLevel || firstRow.StartLevel || firstRow.start_level || 0);
  const firstKm = positions && positions.length > 0 ? Number(positions[0].distance ?? positions[0].Distance ?? positions[0].kmAccum ?? positions[0].KmAccum ?? positions[0].km_accum ?? 0) : 0;

  const eventPoints = [...events]
    .sort((a, b) => moment(a.time, 'HH:mm DD/MM/YYYY') - moment(b.time, 'HH:mm DD/MM/YYYY'))
    .map(e => ({ time: moment(e.time, 'HH:mm DD/MM/YYYY').unix(), type: 'event', data: e }));

  let speedPoints = [];
  if (positions && positions.length > 0) {
    const step = Math.max(1, Math.ceil(positions.length / 150));
    speedPoints = positions
      .filter((_, i) => i % step === 0)
      .map(p => ({ time: p.created_at, type: 'speed', data: p }));
  }

  let oilPoints = [];
  if (oilRows && oilRows.length > 0) {
    const step = Math.max(1, Math.ceil(oilRows.length / 150));
    oilPoints = oilRows
      .filter((_, i) => i % step === 0)
      .map(o => ({ time: o["100"], type: 'oil', data: o }));
  }

  const allPoints = [...speedPoints, ...eventPoints, ...oilPoints].sort((a, b) => a.time - b.time);

  const labels = [];
  const fuelData = [];
  const kmData = [];
  const speedData = [];
  const rawPoints = [];

  labels.push(moment.unix(firstTime).format(formatStr));
  fuelData.push(parseFloat(startLevel.toFixed(2)));
  kmData.push(0);
  speedData.push(null);
  rawPoints.push({ time: firstTime, fuel: startLevel, Km: 0, speed: null, address: null, engineStatus: null, type: 'boundary' });

  let currentFuel = startLevel;
  let currentKm = 0;
  let currentAddress = null;
  let currentEngineStatus = null;

  allPoints.forEach(pt => {
    const tLabel = moment.unix(pt.time).format(formatStr);

    if (pt.type === 'event') {
      const e = pt.data;
      const levelBefore = parseFloat(Number(e.levelBefore || e.LevelBefore || e.level_before || 0).toFixed(2));
      const levelAfter = parseFloat(Number(e.levelAfter || e.LevelAfter || e.level_after || 0).toFixed(2));
      const Km = parseFloat(Number(e.distance ?? e.Distance ?? e.kmAccum ?? e.KmAccum ?? e.km_accum ?? 0).toFixed(2));
      const addr = e.address || e.Address || e.addr || currentAddress || null;
      const evType = levelAfter >= levelBefore ? 'fill' : 'drain';

      currentAddress = addr;

      labels.push(tLabel); fuelData.push(levelBefore); kmData.push(Km - firstKm); speedData.push(null);
      rawPoints.push({ time: pt.time, fuel: levelBefore, Km: Km - firstKm, speed: null, address: addr, engineStatus: null, type: evType });
      labels.push(tLabel + "'"); fuelData.push(levelAfter); kmData.push(Km - firstKm); speedData.push(null);
      rawPoints.push({ time: pt.time, fuel: levelAfter, Km: Km - firstKm, speed: null, address: addr, engineStatus: null, type: evType });

      currentFuel = levelAfter;
      currentKm = Km;
    } else if (pt.type === 'speed') {
      const e = pt.data;

      const fuel = (oilRows && oilRows.length > 0) ? currentFuel : (e.levelFuel || e.LevelFuel || e.level_fuel || currentFuel || 0);
      const Km = e.distance ?? e.Distance ?? e.kmAccum ?? e.KmAccum ?? e.km_accum ?? currentKm ?? 0;
      const spd = e.speedGps != null ? Math.round(e.speedGps) : null;
      const addr = e.address || e.Address || e.addr || currentAddress || null;
      const eng = e.engineStatus != null ? e.engineStatus : currentEngineStatus;

      currentFuel = parseFloat(Number(fuel).toFixed(2));
      currentKm = Km;
      currentAddress = addr;
      currentEngineStatus = eng;

      labels.push(tLabel);
      fuelData.push(currentFuel);
      kmData.push(parseFloat((currentKm - firstKm).toFixed(2)));
      speedData.push(spd);
      rawPoints.push({
        time: pt.time,
        fuel: currentFuel,
        Km: parseFloat((currentKm - firstKm).toFixed(2)),
        speed: e.speedGps,
        address: addr,
        engineStatus: eng,
        type: 'speed',
      });
    } else if (pt.type === 'oil') {
      const o = pt.data;
      const fuelValue = parseFloat(Number(o.oil_cap || 0).toFixed(2));
      currentFuel = fuelValue;

      labels.push(tLabel);
      fuelData.push(currentFuel);
      kmData.push(parseFloat((currentKm - firstKm).toFixed(2)));
      speedData.push(null);
      rawPoints.push({
        time: pt.time,
        fuel: currentFuel,
        Km: parseFloat((currentKm - firstKm).toFixed(2)),
        speed: null,
        address: currentAddress,
        engineStatus: currentEngineStatus,
        type: 'oil',
      });
    }
  });

  const lastRow = summaryRows[summaryRows.length - 1];
  const totalKm = summaryRows.reduce((s, r) => s + Number(r.totalKmGPS || r.TotalKmGPS || r.total_km_gps || 0), 0);
  const endLevel = lastRow ? Number(lastRow.endLevel || lastRow.EndLevel || lastRow.end_level || 0) : startLevel;
  labels.push(moment.unix(lastTime).format(formatStr));
  fuelData.push(parseFloat(endLevel.toFixed(2)));
  kmData.push(parseFloat(totalKm.toFixed(2)));
  speedData.push(null);
  rawPoints.push({ time: lastTime, fuel: endLevel, Km: totalKm, speed: null, address: null, engineStatus: null, type: 'boundary' });

  return { labels, fuelData, kmData, speedData, rawPoints };
};

const useStyles = makeStyles(() => ({
  '@global': {
    '@media print': {
      '@page': {
        size: 'landscape',
      },
      body: {
        background: '#fff !important',
        margin: 0,
      },
      '#root, .MuiBackdrop-root': {
        display: 'none !important',
      },
      '.MuiDialog-root': {
        position: 'absolute !important',
        top: 0,
        left: 0,
        width: '100% !important',
        height: '100% !important',
        zIndex: 9999,
      },
      '.MuiDialog-container': {
        height: '100% !important',
        display: 'flex !important',
      },
      '.MuiDialog-paper': {
        margin: '0 !important',
        padding: '0 !important',
        maxWidth: 'none !important',
        width: '100% !important',
        height: '100vh !important',
        maxHeight: 'none !important',
        boxShadow: 'none !important',
        borderRadius: '0 !important',
        overflow: 'hidden !important',
      },
    },
  },
  paper: {
    borderRadius: '16px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '92vh',
    boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
    padding: '10px 20px',
    flexShrink: 0,
  },
  topBarTitle: {
    color: '#1e293b',
    fontWeight: 700,
    fontSize: '14px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  closeBtn: {
    color: '#64748b',
    '&:hover': { color: '#D9042F' },
    '@media print': { display: 'none !important' },
  },
  layout: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  sidebar: {
    width: '270px',
    flexShrink: 0,
    background: '#f8fafc',
    borderRight: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    paddingBottom: '16px',
  },
  sideSection: {
    padding: '14px 16px',
    borderBottom: '1px solid #e2e8f0',
  },
  sideSectionTitle: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#475569',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '10px',
  },
  darkLabel: {
    fontSize: '11px',
    color: '#475569',
    marginBottom: '4px',
    display: 'block',
    fontWeight: 600,
  },
  darkInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#fff',
      fontSize: '12px',
      color: '#1e293b',
      height: '34px',
      width: '100%',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
    '& .MuiInputBase-input': { color: '#1e293b' },
    '& .MuiInputAdornment-root button': { color: '#64748b' },
    '& .MuiSvgIcon-root': { color: '#64748b', fontSize: '16px' },
    marginBottom: '10px',
    width: '100%',
  },
  autocompleteRoot: {
    marginBottom: '10px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#fff',
      fontSize: '12px',
      height: '34px',
      padding: '0 8px !important',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
    '& .MuiAutocomplete-popupIndicator': { color: '#64748b' },
    '& .MuiInputBase-input': { color: '#1e293b' },
  },
  searchBtn: {
    width: '100%',
    height: '34px',
    borderRadius: '8px',
    background: '#D9042F',
    color: '#fff',
    fontWeight: 700,
    fontSize: '12px',
    textTransform: 'none',
    marginTop: '2px',
    '&:hover': { background: '#B10325' },
    '&:disabled': { opacity: 0.6, color: '#fff' },
  },
  statRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    gap: '12px',
    borderRadius: '8px',
    marginBottom: '8px',
    background: '#fff',
    border: '1px solid #e2e8f0',
    position: 'relative',
    overflow: 'hidden',
  },
  statIconWrap: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#fff',
  },
  statVal: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#1e293b',
    lineHeight: 1.2,
  },
  statLbl: {
    fontSize: '10px',
    marginTop: '2px',
    color: '#64748b',
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
  },
  mainArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#f8fafc',
    overflow: 'hidden',
  },
  chartTopBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 20px',
    background: '#fff',
    borderBottom: '1px solid #e8eaed',
    gap: '6px',
    flexWrap: 'wrap',
    flexShrink: 0,
  },
  timeLbl: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#94a3b8',
    marginRight: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  pill: {
    height: '26px',
    borderRadius: '20px',
    border: '1.5px solid #e2e8f0',
    background: 'transparent',
    color: '#64748b',
    fontSize: '11px',
    fontWeight: 600,
    padding: '0 12px',
    minWidth: 'auto',
    textTransform: 'none',
    '&:hover': { borderColor: '#D9042F', color: '#D9042F' },
  },
  pillActive: {
    height: '26px',
    borderRadius: '20px',
    border: '1.5px solid #D9042F',
    background: '#D9042F',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700,
    padding: '0 12px',
    minWidth: 'auto',
    textTransform: 'none',
    '&:hover': { background: '#B10325', borderColor: '#B10325', color: '#fff' },
  },
  legendSep: {
    width: '1px',
    height: '20px',
    background: '#e2e8f0',
    margin: '0 8px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '11px',
    color: '#475569',
    fontWeight: 600,
  },
  legendDash: {
    width: '16px',
    height: '3px',
    borderRadius: '2px',
  },
  chartWrap: {
    flex: 1,
    padding: '14px 20px 10px 20px',
    minHeight: 0,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  chartInner: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #fde68a',
    flex: 1,
    minHeight: 0,
    padding: '14px',
  },
  emptyChart: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#94a3b8',
    gap: '8px',
  },

  detailStrip: {
    background: '#fff',
    borderTop: '1px solid #e2e8f0',
    padding: '0 20px',
    flexShrink: 0,
  },
  detailInner: {
    display: 'flex',
    alignItems: 'stretch',
    minHeight: '76px',
  },
  detailAddrCol: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px 10px 0',
    borderRight: '1px solid #e2e8f0',
    width: '260px',
    flexShrink: 0,
  },
  detailAddrText: {
    fontSize: '11px',
    color: '#475569',
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  detailCols: {
    display: 'flex',
    flex: 1,
  },
  detailCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '8px 16px',
    borderRight: '1px solid #e2e8f0',
    flex: 1,
    minWidth: '110px',
    '&:last-child': { borderRight: 'none' },
  },
  detailCellKey: {
    fontSize: '10px',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  detailCellVal: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#1e293b',
    marginTop: '2px',
  },
  footer: {
    background: '#fff',
    borderTop: '1px solid #e8eaed',
    padding: '10px 20px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    flexShrink: 0,
    '@media print': {
      display: 'none !important',
    },
  },
  btnPrint: {
    borderRadius: '8px',
    border: '1.5px solid #D9042F',
    color: '#D9042F',
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: 700,
    padding: '5px 18px',
    '&:hover': { background: '#fff0f0' },
  },
  btnClose: {
    borderRadius: '8px',
    border: '1.5px solid #e2e8f0',
    color: '#64748b',
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: 600,
    padding: '5px 18px',
    '&:hover': { background: '#f8fafc' },
  },
  chartRow: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  legendPanel: {
    width: '148px',
    flexShrink: 0,
    background: '#fff',
    borderLeft: '1px solid #e2e8f0',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    overflowY: 'auto',
  },
  legendPanelTitle: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#64748b',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    textAlign: 'left',
    display: 'block',
    marginBottom: '4px',
  },
  legendBoxItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '10px',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'opacity 0.2s',
  },
  legendBoxPreview: {
    width: '36px',
    height: '36px',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden',
    flexShrink: 0,
  },
  legendBoxLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#475569',
  },
}));

const STAT_CONFIG = [
  { key: 'engineOn', label: 'TG bật máy', icon: <TimerIcon style={{ fontSize: 16 }} />, bg: 'linear-gradient(135deg,#065f46,#10b981)' },
  { key: 'Km', label: 'Km di chuyển', icon: <DirectionsCarIcon style={{ fontSize: 16 }} />, bg: 'linear-gradient(135deg,#B91C1C, #E53935)' },
  { key: 'consumed', label: 'Lít tiêu hao', icon: <LocalGasStationIcon style={{ fontSize: 16 }} />, bg: 'linear-gradient(135deg,#3B82F6,#60A5FA)' },
  { key: 'normPerKm', label: 'Định mức/Km', icon: <SpeedIcon style={{ fontSize: 16 }} />, bg: 'linear-gradient(135deg,#B45309,#F5C518)' },
  { key: 'fillCount', label: 'Số lần đổ', icon: <TrendingUpIcon style={{ fontSize: 16 }} />, bg: 'linear-gradient(135deg,#B45309,#F59E0B)' },
  { key: 'drainCount', label: 'Số lần hút', icon: <TrendingDownIcon style={{ fontSize: 16 }} />, bg: 'linear-gradient(135deg,#991B1B,#EF4444)' },
];



const TIME_OPTS = ['5 phút', '30 phút', '1 Giờ', '4 Giờ', '8 Giờ', 'Tất cả'];

const smoothData = (data, windowSize = 5) => {
  if (!data || data.length === 0) return [];
  if (windowSize <= 1) return data;

  const half = Math.floor(windowSize / 2);
  const medianFiltered = [];

  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - half);
    const end = Math.min(data.length - 1, i + half);

    const windowValues = [];
    for (let j = start; j <= end; j++) {
      const val = Number(data[j].oil_cap);
      if (!isNaN(val) && val > 0) {
        windowValues.push(val);
      }
    }

    let median;
    if (windowValues.length > 0) {
      windowValues.sort((a, b) => a - b);
      median = windowValues[Math.floor(windowValues.length / 2)];
    } else {
      median = Number(data[i].oil_cap) || 0;
    }

    medianFiltered.push({
      ...data[i],
      oil_cap: median
    });
  }

  const result = [];
  for (let i = 0; i < medianFiltered.length; i++) {
    const start = Math.max(0, i - half);
    const end = Math.min(medianFiltered.length - 1, i + half);

    let sum = 0;
    let count = 0;
    for (let j = start; j <= end; j++) {
      const val = medianFiltered[j].oil_cap;
      if (val > 0) {
        sum += val;
        count++;
      }
    }

    let average;
    if (count > 0) {
      average = parseFloat((sum / count).toFixed(2));
    } else {
      average = medianFiltered[i].oil_cap;
    }

    result.push({
      ...data[i],
      oil_cap: average
    });
  }

  return result;
};



const ActivityChartModal = ({ open, handleClose, defaultVehicle }) => {
  const cls = useStyles();
  const dispatch = useDispatch();

  const { userId, driverId, agencyId: authAgencyId, adminId } = useSelector(s => s.authSlice);
  const resolvedAgencyId = userId || driverId || authAgencyId || adminId || '';

  const [dateRange, setDateRange] = useState({
    start: moment().subtract(1, 'hours'),
    end: moment(),
  });
  const [licensePlate, setLicensePlate] = useState('');
  const [activeTime, setActiveTime] = useState('1 Giờ');

  const [summaryRows, setSummaryRows] = useState([]);
  const [fillDrainRows, setFillDrainRows] = useState([]);
  const [positionRows, setPositionRows] = useState([]);
  const [oilRows, setOilRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastDetailEvent, setLastDetailEvent] = useState(null);
  const [error, setError] = useState('');
  const [hiddenSeries, setHiddenSeries] = useState([false, false, false]);
  const chartRef = React.useRef(null);
  const [hoverDataIndex, setHoverDataIndex] = useState(null);
  const rawPointsRef = React.useRef([]);

  const handleSearch = async (customPlate, customStart, customEnd) => {
    const p = (typeof customPlate === 'string') ? customPlate : licensePlate;
    const s = (customStart && moment.isMoment(customStart)) ? customStart : dateRange.start;
    const e = (customEnd && moment.isMoment(customEnd)) ? customEnd : dateRange.end;

    if (!p) {
      setError('Vui lòng nhập biển số');
      return;
    }

    const firstTime = moment(s).unix();
    const lastTime = moment(e).unix();
    const params = { licensePlates: [p], firstTime, lastTime, agencyId: resolvedAgencyId, pageSize: 500 };
    const deviceId = defaultVehicle?.device_id;

    setIsLoading(true);
    try {
      const [summaryRes, fillDrainRes, positionRes, oilRes] = await Promise.all([
        dispatch(reportFuelSummary(params)),
        dispatch(reportFuelDrainFill({ ...params, page: 1 })),
        deviceId
          ? dispatch(getDetailDevicePosition({ id: deviceId, first_time: firstTime, last_time: lastTime }))
          : Promise.resolve(null),
        deviceId
          ? dispatch(getOilStatistic({ id: deviceId, first_time: firstTime, last_time: lastTime }))
          : Promise.resolve(null),
      ]);

      let summaryData = [];
      if (summaryRes?.payload?.data) summaryData = summaryRes.payload.data;
      else if (Array.isArray(summaryRes?.payload)) summaryData = summaryRes.payload;
      else if (summaryRes?.payload && !Array.isArray(summaryRes.payload)) summaryData = [summaryRes.payload];

      let fillDrainData = [];
      if (fillDrainRes?.payload?.data) fillDrainData = fillDrainRes.payload.data;
      else if (Array.isArray(fillDrainRes?.payload)) fillDrainData = fillDrainRes.payload;

      const positionData = positionRes?.payload?.payload?.path || [];
      const oilData = oilRes?.payload?.payload?.result || [];

      setSummaryRows(summaryData);
      setFillDrainRows(fillDrainData);
      setPositionRows(positionData);
      setOilRows(oilData);

      if (fillDrainData.length > 0) {
        setLastDetailEvent(fillDrainData[fillDrainData.length - 1]);
      } else {
        setLastDetailEvent(null);
      }
    } catch (e) {
      setError('Không thể tải dữ liệu. Vui lòng thử lại.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimePresetClick = (t) => {
    setActiveTime(t);
    let newStart = moment(dateRange.start);
    const newEnd = moment();

    if (t === 'Tất cả') {
      newStart = moment().startOf('day');
    } else if (t === '5 phút') {
      newStart = moment().subtract(5, 'minutes');
    } else if (t === '30 phút') {
      newStart = moment().subtract(30, 'minutes');
    } else if (t === '1 Giờ') {
      newStart = moment().subtract(1, 'hours');
    } else if (t === '4 Giờ') {
      newStart = moment().subtract(4, 'hours');
    } else if (t === '8 Giờ') {
      newStart = moment().subtract(8, 'hours');
    }

    setDateRange({ start: newStart, end: newEnd });
    handleSearch(licensePlate, newStart, newEnd);
  };

  const handlePrint = () => {
    window.print();
  };


  const hasInitialized = React.useRef(false);

  useEffect(() => {
    if (open && !hasInitialized.current) {
      const plate = defaultVehicle?.license_plate || '';
      setLicensePlate(plate);

      const end = moment();
      const start = moment().subtract(1, 'hours');
      setDateRange({ start, end });
      setActiveTime('1 Giờ');

      if (plate) {
        handleSearch(plate, start, end);
      }
      hasInitialized.current = true;
    } else if (!open && hasInitialized.current) {
      hasInitialized.current = false;
    }
  }, [open, defaultVehicle?.license_plate]);

  useEffect(() => {
    setHoverDataIndex(null);
  }, [positionRows, oilRows, fillDrainRows]);

  const processedData = useMemo(() => {
    if (!fillDrainRows.length && !summaryRows.length && !positionRows.length && !oilRows.length) return null;
    const firstTime = moment(dateRange.start).unix();
    const lastTime = moment(dateRange.end).unix();
    const smoothedOilRows = smoothData(oilRows, 5);
    const res = buildChartFromEvents(fillDrainRows, summaryRows, positionRows, smoothedOilRows, firstTime, lastTime);
    rawPointsRef.current = res.rawPoints;
    return res;
  }, [fillDrainRows, summaryRows, positionRows, oilRows, dateRange]);

  const stats = useMemo(() => {
    if (!processedData) {
      return { Km: '--', engineOn: '--', normPerKm: '--', consumed: '--', fillCount: '--', drainCount: '--' };
    }

    const { rawPoints } = processedData;
    const lastPoint = rawPoints.length > 0 ? rawPoints[rawPoints.length - 1] : null;
    const totalKm = lastPoint?.Km || 0;

    const totalEngineMin = summaryRows.reduce((s, r) => s + parseEngineOnTime(r.engineOnTime || r.EngineOnTime || r.engine_on_time), 0);
    const totalConsumed = summaryRows.reduce((s, r) => {
      const consumption = Number(r.actConsumption || r.fuelConsumed || r.ActConsumption || r.FuelConsumed || r.act_consumption || r.fuel_consumed || 0);
      if (consumption > 0) return s + consumption;
      const start = Number(r.startLevel || r.StartLevel || r.start_level || 0);
      const end = Number(r.endLevel || r.EndLevel || r.end_level || 0);
      const fill = Number(r.totalFilled || r.TotalFilled || r.total_filled || 0);
      const calculated = (start + fill) - end;
      return s + (calculated > 0 ? calculated : 0);
    }, 0);

    const totalFill = summaryRows.reduce((s, r) => s + (Number(r.fillCount || r.FillCount || r.fill_count || 0)), 0);
    const totalDrain = summaryRows.reduce((s, r) => s + (Number(r.drainCount || r.DrainCount || r.drain_count || 0)), 0);
    const normPerKm = totalKm > 0 ? totalConsumed / totalKm : 0;

    return {
      Km: `${totalKm.toFixed(2)} Km`,
      engineOn: totalEngineMin > 0 ? formatMinutes(totalEngineMin) : '00:00',
      normPerKm: normPerKm > 0 ? `${normPerKm.toFixed(3)} lít/Km` : '0.000 lít/Km',
      consumed: `${totalConsumed.toFixed(2)} lít`,
      fillCount: String(totalFill),
      drainCount: String(totalDrain),
    };
  }, [processedData, summaryRows]);


  const { dynamicFuelMax, dynamicSpeedMax } = useMemo(() => {
    if (!processedData) return { dynamicFuelMax: 600, dynamicSpeedMax: 720 };
    const oilCap = defaultVehicle?.oil_cap || 0;
    const dataMaxF = Math.max(...processedData.fuelData, 100, oilCap);
    const fuelMax = Math.ceil(dataMaxF / 100) * 100;
    const finalFuelMax = Math.max(fuelMax, 600);
    return {
      dynamicFuelMax: finalFuelMax,
      dynamicSpeedMax: finalFuelMax * 1.2
    };
  }, [processedData]);

  const chartData = useMemo(() => {
    if (!processedData) return null;
    const { labels, fuelData, kmData, speedData } = processedData;
    return {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Nhiên liệu (lít)',
          data: fuelData,
          borderColor: '#60A5FA',
          backgroundColor: '#60A5FA',
          hoverBackgroundColor: '#60A5FA',
          hoverBorderWidth: 0,
          borderWidth: 0,
          borderSkipped: false,
          barPercentage: 1.0,
          categoryPercentage: 1.0,
          yAxisID: 'yFuel',
          order: 3,
        },
        {
          type: 'line',
          label: 'Km',
          data: kmData,
          borderColor: '#E53935',
          backgroundColor: 'transparent',
          borderWidth: 2,
          fill: false,
          yAxisID: 'yKm',
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.4,
          spanGaps: true,
          order: 1,
        },
        {
          type: 'line',
          label: 'Vận tốc (Km/h)',
          data: speedData,
          borderColor: '#F5C518',
          backgroundColor: 'rgba(245,197,24,0.08)',
          borderWidth: 2,
          fill: false,
          yAxisID: 'ySpeed',
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.4,
          spanGaps: true,
          order: 2,
        },
      ],
    };
  }, [processedData, oilRows]);

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      onHover: (event, activeElements) => {
        if (activeElements && activeElements.length > 0) {
          const idx = activeElements[0].index;
          setHoverDataIndex(prev => prev !== idx ? idx : prev);
        } else {
          setHoverDataIndex(null);
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        crosshair: {
          line: { color: '#94a3b8', width: 1, dashPattern: [5, 5] },
          sync: { enabled: false },
          zoom: { enabled: false },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#94a3b8', font: { size: 10 }, maxTicksLimit: 12 }
        },
        yFuel: {
          type: 'linear',
          position: 'left',
          title: { display: true, text: 'Nhiên liệu (lít)', color: '#60A5FA', font: { size: 10, weight: 'bold' } },
          grid: { color: '#FFFFFF', z: 1, lineWidth: 0.5 },
          ticks: {
            color: '#94a3b8',
            font: { size: 10 },
            stepSize: 100,
          },
          min: 0,
          max: dynamicFuelMax,
        },
        yKm: {
          type: 'linear',
          position: 'right',
          title: { display: true, text: 'Quãng đường (Km)', color: '#E53935', font: { size: 10, weight: 'bold' } },
          grid: { drawOnChartArea: false },
          ticks: {
            color: '#94a3b8',
            font: { size: 10 },
            stepSize: 100,
          },
          min: 0,
          suggestedMax: 600,
        },
        ySpeed: {
          type: 'linear',
          position: 'right',
          title: { display: true, text: 'Vận tốc (Km/h)', color: '#F5C518', font: { size: 10, weight: 'bold' } },
          grid: { drawOnChartArea: false },
          ticks: {
            display: true,
            color: '#F5C518',
            font: { size: 9 },
            stepSize: 30,
            callback: (value) => (value <= 120 ? value : null),
          },
          min: 0,
          max: dynamicSpeedMax,
        },
      },
    };
  }, [dynamicFuelMax, dynamicSpeedMax]);

  const activePoint = hoverDataIndex !== null ? (rawPointsRef.current[hoverDataIndex] || null) : null;

  const detailCells = [
    { key: 'Thời điểm', val: activePoint ? moment.unix(activePoint.time).format('HH:mm:ss DD/MM/YYYY') : '--' },
    { key: 'Km', val: activePoint?.Km != null ? `${activePoint.Km.toFixed(2)} Km` : '--' },
    { key: 'Vận tốc', val: activePoint?.speed != null ? `${Math.round(activePoint.speed)} Km/h` : '--' },
    { key: 'Nhiên liệu', val: activePoint?.fuel != null ? `${activePoint.fuel.toFixed(2)} lít` : '--' },
    { key: 'Loại sự kiện', val: activePoint?.type === 'fill' ? 'Đổ nhiên liệu' : activePoint?.type === 'drain' ? 'Hút nhiên liệu' : '--' },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{ className: cls.paper }}
      >
        <Box className={cls.topBar}>
          <Box>
            <Typography className={cls.topBarTitle}>Cảm biến nhiên liệu</Typography>
          </Box>
          <IconButton size="small" className={cls.closeBtn} onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className={cls.layout}>

          <Box className={cls.sidebar}>

            <Box className={cls.sideSection}>
              <Typography className={cls.sideSectionTitle}>Bộ lọc tìm kiếm</Typography>

              <span className={cls.darkLabel}>Từ ngày *</span>
              <div style={{ marginBottom: '10px' }}>
                <DateTimeAntd
                  showTime={true}
                  format={'DD/MM/YYYY HH:mm'}
                  dateTime={dateRange.start}
                  onChange={v => setDateRange(p => ({ ...p, start: v }))}
                  onOk={() => { }}
                  width="100%"
                  height="34px"
                  dark={false}
                />
              </div>

              <span className={cls.darkLabel}>Đến ngày *</span>
              <div style={{ marginBottom: '10px' }}>
                <DateTimeAntd
                  showTime={true}
                  format={'DD/MM/YYYY HH:mm'}
                  dateTime={dateRange.end}
                  onChange={v => setDateRange(p => ({ ...p, end: v }))}
                  onOk={() => { }}
                  width="100%"
                  height="34px"
                  dark={false}
                />
              </div>

              <span className={cls.darkLabel}>Biển số *</span>
              <TextField
                variant="outlined"
                size="small"
                value={licensePlate}
                onChange={e => setLicensePlate(e.target.value.toUpperCase())}
                placeholder="VD: 51A12345"
                className={cls.darkInput}
                InputProps={{ style: { color: '#1e293b', fontSize: '12px' } }}
              />

              <Button
                className={cls.searchBtn}
                startIcon={isLoading ? <CircularProgress size={13} style={{ color: '#fff' }} /> : <SearchIcon style={{ fontSize: 16 }} />}
                onClick={() => handleSearch()}
                disabled={isLoading}
              >
                {isLoading ? 'Đang tải...' : 'Tìm kiếm'}
              </Button>
            </Box>
            <Box className={cls.sideSection} style={{ flex: 1, borderBottom: 'none' }}>
              <Typography className={cls.sideSectionTitle}>Số liệu tổng hợp</Typography>
              {STAT_CONFIG.map(s => (
                <Box key={s.key} className={cls.statRow}>
                  <Box className={cls.statIconWrap} style={{ background: s.bg }}>{s.icon}</Box>
                  <Box>
                    <div className={cls.statVal}>{stats[s.key]}</div>
                    <div className={cls.statLbl}>{s.label}</div>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box className={cls.mainArea}>
            <Box className={cls.chartTopBar}>
              <span className={cls.timeLbl}>Mốc:</span>
              {TIME_OPTS.map(t => (
                <Button
                  key={t}
                  className={activeTime === t ? cls.pillActive : cls.pill}
                  onClick={() => handleTimePresetClick(t)}
                >
                  {t}
                </Button>
              ))}
            </Box>
            <Box className={cls.chartRow}>
              <Box className={cls.chartWrap}>
                <Paper className={cls.chartInner} elevation={0}>
                  {chartData ? (
                    <Chart type="bar" ref={chartRef} data={chartData} options={chartOptions} plugins={CHART_PLUGINS} onMouseLeave={() => setHoverDataIndex(null)} />
                  ) : (
                    <Box className={cls.emptyChart}>
                      <InfoOutlinedIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
                      <Typography style={{ fontSize: '13px', color: '#94a3b8' }}>
                        Chọn biển số và nhấn <b>Tìm kiếm</b> để xem biểu đồ
                      </Typography>
                    </Box>
                  )}
                </Paper>
                <Typography style={{ fontSize: '11px', color: '#64748b', textAlign: 'center', fontStyle: 'italic', fontWeight: 600 }}>
                  Biểu đồ tương quan Nhiên liệu - Vận tốc - Km di chuyển
                </Typography>
              </Box>

              {/* Legend Panel */}
              <Box className={cls.legendPanel}>
                <span className={cls.legendPanelTitle}>Bảng ký hiệu màu</span>

                {/* Nhiên liệu */}
                <Box
                  className={cls.legendBoxItem}
                  style={{ opacity: hiddenSeries[0] ? 0.35 : 1 }}
                  onClick={() => {
                    const chart = chartRef.current;
                    if (!chart) return;
                    const meta = chart.getDatasetMeta(0);
                    meta.hidden = !meta.hidden;
                    chart.update();
                    setHiddenSeries(prev => { const n = [...prev]; n[0] = !n[0]; return n; });
                  }}
                >
                  <Box className={cls.legendBoxPreview}>
                    <svg width="100%" height="100%" viewBox="0 0 80 38" preserveAspectRatio="none">
                      <rect x="0" y="0" width="80" height="38" fill="#e8f0fe" />
                      <rect x="5" y="8" width="14" height="30" fill="#60A5FA" />
                      <rect x="22" y="18" width="14" height="20" fill="#60A5FA" />
                      <rect x="39" y="4" width="14" height="34" fill="#60A5FA" />
                      <rect x="56" y="12" width="14" height="26" fill="#60A5FA" />
                    </svg>
                  </Box>
                  <span className={cls.legendBoxLabel} style={{ textDecoration: hiddenSeries[0] ? 'line-through' : 'none' }}>Mức nhiên liệu</span>
                </Box>

                {/* Vận tốc */}
                <Box
                  className={cls.legendBoxItem}
                  style={{ opacity: hiddenSeries[2] ? 0.35 : 1 }}
                  onClick={() => {
                    const chart = chartRef.current;
                    if (!chart) return;
                    const meta = chart.getDatasetMeta(2);
                    meta.hidden = !meta.hidden;
                    chart.update();
                    setHiddenSeries(prev => { const n = [...prev]; n[2] = !n[2]; return n; });
                  }}
                >
                  <Box className={cls.legendBoxPreview}>
                    <svg width="100%" height="100%" viewBox="0 0 80 38" preserveAspectRatio="none">
                      <rect x="0" y="0" width="80" height="38" fill="white" />
                      <polyline points="0,32 18,28 30,10 45,6 58,16 72,30 80,34" fill="none" stroke="#F5C518" strokeWidth="2.5" />
                    </svg>
                  </Box>
                  <span className={cls.legendBoxLabel} style={{ textDecoration: hiddenSeries[2] ? 'line-through' : 'none' }}>Đường vận tốc</span>
                </Box>

                {/* Km */}
                <Box
                  className={cls.legendBoxItem}
                  style={{ opacity: hiddenSeries[1] ? 0.35 : 1 }}
                  onClick={() => {
                    const chart = chartRef.current;
                    if (!chart) return;
                    const meta = chart.getDatasetMeta(1);
                    meta.hidden = !meta.hidden;
                    chart.update();
                    setHiddenSeries(prev => { const n = [...prev]; n[1] = !n[1]; return n; });
                  }}
                >
                  <Box className={cls.legendBoxPreview}>
                    <svg width="100%" height="100%" viewBox="0 0 80 38" preserveAspectRatio="none">
                      <rect x="0" y="0" width="80" height="38" fill="white" />
                      <polyline points="0,34 20,32 42,20 62,10 80,6" fill="none" stroke="#E53935" strokeWidth="2.5" />
                    </svg>
                  </Box>
                  <span className={cls.legendBoxLabel} style={{ textDecoration: hiddenSeries[1] ? 'line-through' : 'none' }}>Đường Km</span>
                </Box>
              </Box>
            </Box>
            <Box className={cls.detailStrip}>
              <Box className={cls.detailInner}>
                <Box className={cls.detailAddrCol}>
                  <LocationOnIcon style={{ color: '#D9042F', fontSize: 15, flexShrink: 0 }} />
                  <Typography className={cls.detailAddrText}>
                    {activePoint?.address || 'Đưa chuột vào biểu đồ để xem chi tiết'}
                  </Typography>
                </Box>
                <Box className={cls.detailCols}>
                  {detailCells.map(c => (
                    <Box key={c.key} className={cls.detailCell}>
                      <span className={cls.detailCellKey}>{c.key}</span>
                      <span className={cls.detailCellVal}>{c.val}</span>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
            <Box className={cls.footer}>
              <Button
                variant="outlined"
                className={cls.btnPrint}
                startIcon={<PrintIcon style={{ fontSize: 15 }} />}
                onClick={handlePrint}
              >
                In báo cáo
              </Button>
              <Button variant="outlined" className={cls.btnClose} onClick={handleClose}>
                Đóng
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError('')}
        message={error}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default ActivityChartModal;
